import React, { useCallback, useReducer } from 'react';
import ReactDOM from 'react-dom';

import styles from './HourSelector.module.css';
import { AiOutlineClockCircle } from 'react-icons/ai';

import { useState } from 'react';
import useClickOutside from '../../../hooks/useClickOutside';
import useTooltip from '../../../hooks/useTooltip';

import InputNumArrows from '../InputNumArrows/InputNumArrows';

import { HH, mm } from '../../../interfaces/Time';
import { ActionTime } from '../../../interfaces/Reducers';

interface TimeState {
    hours: string | null
    minutes: string | null
}
interface Props extends TimeState {
    hours: HH | null
    minutes: mm | null
    handleChange: (hours: HH | null, minutes: mm | null) => void
    style?: React.CSSProperties
}

const parseTimeNumber = (value: number) => (value < 10 ? '0' : '') + value.toString();

const valueLimits = (value: number, min: number, max: number) =>
    value < min ? max : value > max ? min : value;

const valueWithinLimits = (value: number, min: number, max: number) =>
    value < min ? min : value > max ? max : value;

const incrementValue = (oldValue: string | null | undefined, increment: boolean, min: number, max: number) => {
    return oldValue === null || oldValue === undefined ?
        increment ? min.toString() : max.toString()
        :
        parseTimeNumber(valueLimits(Number(oldValue) + (increment ? 1 : -1), min, max));
};

const reducerTime = (state: TimeState, action: ActionTime) => {
    if (action.type === 'increment' || action.type === 'decrement') {
        if (action.unit === 'h') return {
            ...state,
            hours: incrementValue(state.hours, action.type === 'increment', 0, 23) as HH
        };
        if (action.unit === 'm') return {
            ...state,
            minutes: incrementValue(state.minutes, action.type === 'increment', 0, 59) as mm
        };
    }
    else if (action.type === 'set') {
        const value = action.payload.trim() === '' ? null : Number(action.payload);
        return {
            ...state,
            hours: action.unit === 'h' ? value === null ? null :
                valueWithinLimits(value, 0, 23).toString() : state.hours,
            minutes: action.unit === 'm' ? value === null ? null :
                valueWithinLimits(value, 0, 59).toString() : state.minutes,
        };
    }
    return state;
};

const HourSelector: React.FC<Props> = ({ hours, minutes, handleChange, style }) => {

    const [showSelector, setShowSelector] = useState(false);

    const toggleSelector = () => {
        if (showSelector) saveData();
        setShowSelector(show => !show);
    };

    const [time, dispatchTime] = useReducer(reducerTime, { hours, minutes });

    const getStylePos = (pos: DOMRect, element: HTMLInputElement) => {
        return {
            top: `${pos.bottom}px`,
            left: `${pos.left + element.offsetWidth / 2}px`,
        };
    };

    const { tooltipRef, getStyles } = useTooltip(getStylePos);

    const saveData = useCallback(() => {
        const { hours, minutes } = time;
        const parsedHours = typeof hours === 'string' ?
            parseTimeNumber(Number(hours)) as HH : hours;
        const parsedMinutes = typeof minutes === 'string' ?
            parseTimeNumber(Number(minutes)) as mm : minutes;
        handleChange(parsedHours, parsedMinutes);
    }, [handleChange, time]);

    const onClickOutside = useCallback(() => {
        setShowSelector(false);
        saveData();
    }, [saveData]);

    const wrapperRef = useClickOutside(onClickOutside, tooltipRef);

    return (
        <div ref={tooltipRef} className={styles.container}>
            <div className={styles.button} onClick={toggleSelector} style={style}>
                <span>{hours === null ? 'HH' : parseTimeNumber(Number(hours))}{minutes !== undefined ? (hours !== undefined ? ':' : '') + (minutes === null ? 'MM' : parseTimeNumber(Number(minutes))) : ''}</span>
                <AiOutlineClockCircle />
            </div>
            {showSelector ?
                ReactDOM.createPortal(
                    <div
                        style={{
                            ...getStyles(),
                            position: 'absolute'
                        }}
                        ref={wrapperRef}
                    >
                        <div className={styles.selector}>
                            <InputNumArrows
                                value={time.hours}
                                unit='h'
                                dispatchTime={dispatchTime}
                            />
                            <p>:</p>
                            <InputNumArrows
                                value={time.minutes}
                                unit='m'
                                dispatchTime={dispatchTime}
                            />
                        </div>
                        <div className={styles.selectorOutterArrow}>
                            <div className={styles.selectorArrow}></div>
                        </div>
                    </div>
                    , document.getElementById('tooltip') as HTMLElement)
                : null
            }
        </div>
    );
};

export default HourSelector;